import type { MetadataKey } from '@mimir/primitive/premiere/premiereInfo';
import type { ProjectItemUpdate } from '@mimir/primitive/premiere/premiereUpdate';
import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { invariant } from '@mimir/common/util/invariant';
import { importToPremiereAsync } from './importToPremiere';
import { type AdobePanelContext, type ElectronContext, isInAdobeApp, isInElectron } from '@mimir/common/context/applicationContext';
import { convertSecondsToTicks } from '@mimir/client-common/util/premiere';

export type SubclipDownload = OngoingDownload & Required<Pick<OngoingDownload, 'subclip' | 'title'>>;


export async function importSubclip(
  applicationContext: AdobePanelContext | ElectronContext,
  ongoingDownload: SubclipDownload,
  locallyAvailablePath: string,
  shouldUsePremierePreferences: boolean,
): Promise<void> {
  const subclip = ongoingDownload.subclip;
  invariant(subclip, 'Expected subclip in importSubclip');
  const artRef = ongoingDownload.artifactReference;
  if (artRef?.type == 'TsSegmentRange') {
    if (isInAdobeApp(applicationContext)) {
      const cepb = applicationContext.cepBridgeClient;
      const imported = await cepb.call('importProjectFiles', [locallyAvailablePath], shouldUsePremierePreferences);
      const ref = imported[locallyAvailablePath];
      invariant(ref, 'Expected reference to imported file');

      const isProxy = ongoingDownload.artefact == 'proxy';
      const meta: Partial<Record<MetadataKey, string>> = {
        'Mimir.Item.Id': ongoingDownload.itemId,
        'Mimir.Artifact': JSON.stringify(artRef),
        'Column.Intrinsic.TapeName': ongoingDownload.originalFileName
      };
      const update: ProjectItemUpdate = {
        name: ongoingDownload.title,
        meta,
        proxyMediaPath: isProxy ? locallyAvailablePath : null,
        hiresMediaPath: isProxy ? null : locallyAvailablePath,
        markerInfos: ongoingDownload.markers,
        startTimeInTicks: null,
      };
      await cepb.call('updateProjectItem', ref, update);

      const subclipRef = await cepb.call('createSubclip', ref, subclip.title, subclip.startTime, subclip.endTime);
      await cepb.call(
        'updateProjectItem',
        subclipRef,
        {
          meta,
          startTimeInTicks: subclip.premiereStartTimeSeconds ? convertSecondsToTicks(subclip.premiereStartTimeSeconds) : null,
          timeDisplayFormat: ongoingDownload.premiereTimeDisplayFormat,
        }
      );

      // Once we have created the subclip we delete the main clip so as to
      // not pollute the bin
      await cepb.call('deleteProjectItems', [ref]);

      // Re-select the subclip since deleteProjectItems above can make it lose the selection.
      await cepb.call('selectProjectItem', subclipRef);

      if (ongoingDownload.insertIntoSequence) {
        await cepb.call('insertClipIntoCurrentOrNewSequenceAndUpdatePlayerPosition', subclipRef);
      }
    } else if (isInElectron(applicationContext)) {
      await importToPremiereAsync(
        applicationContext,
        {
          filePath: locallyAvailablePath,
          title: ongoingDownload.title,
          subclipTitle: subclip.title,
          subclipId: subclip.id,
          startTime: subclip.startTime,
          endTime: subclip.endTime,
          sourceItemId: ongoingDownload.itemId,
          proxyFilePath: ongoingDownload.artefact === 'proxy' ? locallyAvailablePath : undefined,
          tapeName: ongoingDownload.originalFileName,
          artifactReference:  ongoingDownload.artifactReference ?? null,
          insertIntoSequence: ongoingDownload.insertIntoSequence,
          markers: ongoingDownload.markers,
          startTimeInTicks: ongoingDownload.premiereStartTimeSeconds ? convertSecondsToTicks(ongoingDownload.premiereStartTimeSeconds) : null,
          timeDisplayFormat: ongoingDownload.premiereTimeDisplayFormat ?? null,
          mediaFrameRate: ongoingDownload.mediaFrameRate,
          mogrtChildrenMap: ongoingDownload.mogrtChildrenMap,
          timelineInsertion: ongoingDownload.timelineInsertion ?? null,
        },
        shouldUsePremierePreferences
      );
    }

    return;
  }
  const mimirId = ongoingDownload.isSubclipItem ? '' : ongoingDownload.itemId;
  await importToPremiereAsync(
    applicationContext,
    {
      filePath: locallyAvailablePath,
      title: ongoingDownload.title,
      subclipTitle: subclip.title,
      subclipId: subclip.id,
      startTime: subclip.startTime,
      endTime: subclip.endTime,
      sourceItemId: mimirId,
      proxyFilePath: ongoingDownload.artefact === 'proxy' ? locallyAvailablePath : undefined,
      tapeName: ongoingDownload.originalFileName,
      artifactReference:  ongoingDownload.artifactReference ?? null,
      insertIntoSequence: ongoingDownload.insertIntoSequence,
      markers: ongoingDownload.markers,
      startTimeInTicks: ongoingDownload.premiereStartTimeSeconds ? convertSecondsToTicks(ongoingDownload.premiereStartTimeSeconds) : null,
      timeDisplayFormat: ongoingDownload.premiereTimeDisplayFormat ?? null,
      mediaFrameRate: ongoingDownload.mediaFrameRate,
      mogrtChildrenMap: ongoingDownload.mogrtChildrenMap,
      timelineInsertion: ongoingDownload.timelineInsertion ?? null,
    },
    shouldUsePremierePreferences,
    ongoingDownload.targetBin,
  );
}
